import React, {useCallback, useEffect, useState} from "react";
import {getAllMenus} from "../../../../api/menu/MenuService";
import UserGroupMenuTreeNode from "./UserGroupMenus";
import {selectGroupMenus} from "../../../../api/menu/GroupMenuService";
import {updateUserGroupMenusByUserGroup} from "../../../../api/usergroup/UserGroupService";
import {handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {apiLoading} from "../../../../redux/slice/apiSlice";
import {isAdmin} from "../../../../components/commonmethod/CommonMethod";
import "../userGroupMenuSetting.css";

function collectAllMenuIds(menuList) {
    let ids = [];
    menuList.forEach((menu) => {
        ids.push(menu.id);
        if (menu.children?.length) {
            ids = [...ids, ...collectAllMenuIds(menu.children)];
        }
    });
    return ids;
}

function findAllChildIds(menu) {
    let ids = [...menu.children?.map((child) => child.id) || []];
    menu.children?.forEach((child) => {
        ids = [...ids, ...findAllChildIds(child)];
    });
    return ids;
}

function findAncestorIds(menuList, targetId, ancestors = []) {
    for (const menu of menuList) {
        if (menu.id === targetId) {
            return ancestors;
        }
        if (menu.children?.length) {
            const found = findAncestorIds(menu.children, targetId, [...ancestors, menu.id]);
            if (found) {
                return found;
            }
        }
    }
    return null;
}

const UserGroupMenuSetting = ({data}) => {
    const [menus, setMenus] = useState([]);
    const [groupMenus, setGroupMenus] = useState([]);

    const {t} = useTranslation();
    const userAuth = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();

    const loadMenus = useCallback(async () => {
        if (!data?.id) {
            setMenus([]);
            setGroupMenus([]);
            return;
        }

        dispatch(apiLoading(true));
        try {
            const [responseMenus, responseUserGroupMenus] = await Promise.all([
                getAllMenus(),
                selectGroupMenus(data.id),
            ]);

            if (responseMenus?.status === 200) {
                setMenus([...responseMenus.data]);
            }

            if (responseUserGroupMenus?.status === 200) {
                setGroupMenus([...responseUserGroupMenus.data.menuIds]);
            }
        } finally {
            dispatch(apiLoading(false));
        }
    }, [data?.id, dispatch]);

    useEffect(() => {
        void loadMenus();
    }, [loadMenus]);

    const onChangeHandler = (menu) => {
        const childIds = findAllChildIds(menu);
        const ancestorIds = findAncestorIds(menus, menu.id) ?? [];

        setGroupMenus((prev) => {
            if (prev.includes(menu.id)) {
                const removeIds = new Set([menu.id, ...childIds]);
                return prev.filter((menuId) => !removeIds.has(menuId));
            }

            const next = new Set(prev);
            next.add(menu.id);
            ancestorIds.forEach((ancestorId) => next.add(ancestorId));
            return [...next];
        });
    };

    const totalHandler = () => {
        const allIds = collectAllMenuIds(menus);
        setGroupMenus((prev) => (prev.length === allIds.length ? [] : allIds));
    };

    const handleSaveUserGroupMenu = async () => {
        if (!window.confirm(t("confirmUpdate"))) {
            return;
        }

        await updateUserGroupMenusByUserGroup(
            {userGroupId: data.id, menuIds: groupMenus},
            () => {
                handleActiveToast({message: t("updated")});
            },
        );
    };

    const allIds = collectAllMenuIds(menus);
    const isAllSelected = allIds.length > 0 && groupMenus.length === allIds.length;

    return (
        <div className="detail-view-tab-panel user-group-menu-setting code-manage-scope">
            <div className="user-group-menu-setting__toolbar">
                <button
                    type="button"
                    className="code-manage-confirm-btn"
                    onClick={totalHandler}
                >
                    {isAllSelected ? "전체 해제" : "전체 선택"}
                </button>
                {isAdmin(userAuth) && (
                    <div className="user-group-menu-setting__toolbar-actions">
                        <button
                            type="button"
                            className="save-btn"
                            onClick={() => void handleSaveUserGroupMenu()}
                        >
                            저장
                        </button>
                    </div>
                )}
            </div>

            <div className="user-group-menu-setting__panel">
                <div className="user-group-menu-setting__scroll">
                    {menus.length === 0 ? (
                        <div className="user-group-menu-setting__empty" role="status">
                            등록된 메뉴가 없습니다.
                        </div>
                    ) : (
                        menus.map((menu) => (
                            <UserGroupMenuTreeNode
                                key={menu.id}
                                menu={menu}
                                depth={0}
                                groupMenus={groupMenus}
                                onChangeHandler={onChangeHandler}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserGroupMenuSetting;
